import { yupResolver } from '@hookform/resolvers/yup'
import { EditOutlined, SaveOutlined } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogProps } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { FunctionComponent, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { MyDialogTitle } from '../../../../base/components/Dialog/MyDialogTitle.tsx'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import {
  TIPO_ARTICULO_INITIAL_VALUES,
  TipoArticuloInputProps,
  TipoArticuloProps,
} from '../../../../interfaces/tipoArticulo.ts'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiTipoArticuloActualizar } from '../../api/apiTipoArticuloActualizar'
import { TipoArticuloApiInputProps } from '../../interfaces'
import { tipoArticuloComposeService } from '../../services/tipoArticuloRegistroComposeService.ts'
import { TipoArticuloValidationSchema } from '../../validator/tipoArticuloValidator.ts'
import TipoArticuloForm from '../TipoArticuloForm'

interface OwnProps extends Omit<DialogProps, 'open' | 'onClose'> {
  open: boolean
  tipoArticulo: TipoArticuloInputProps
  onClose: (value?: TipoArticuloProps) => void
}

type Props = OwnProps

/**
 * Formulario para editar un tipo de articulo
 * @param props
 * @constructor
 */
const TipoArticuloActualizarDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, tipoArticulo, ...other } = props
  const form = useForm<TipoArticuloInputProps>({
    values: tipoArticulo || TIPO_ARTICULO_INITIAL_VALUES,
    resolver: yupResolver<any, any, any>(TipoArticuloValidationSchema),
  })
  const { requestConfirm, showError, toast } = useUtils()

  /** Guardamos los datos */
  const { mutate } = useMutation({
    mutationFn: async (data: TipoArticuloApiInputProps) => {
      return await apiTipoArticuloActualizar(data)
    },
    onMutate: () => {
      swalLoading()
    },
    onSuccess: async (resp?: TipoArticuloProps) => {
      toast.success('Actualización correcta')
      onClose(resp)
    },
    onError: (err) => {
      showError(err)
    },
    onSettled: async () => {
      swalClose()
    },
  })

  /**
   * Actualizamos los datos
   * @param values
   */
  const onSubmit: SubmitHandler<TipoArticuloInputProps> = useCallback(
    async (values) => {
      const apiInput = tipoArticuloComposeService(values)

      const { confirmed } = await requestConfirm()
      if (!confirmed) return
      mutate(apiInput)
    },
    [requestConfirm, mutate],
  )

  const onError = (error: any, e: any) => {
    console.error('Error:', error, e)
  }

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '100%', maxHeight: '80vh' } }}
      maxWidth="sm"
      open={open}
      {...other}
    >
      <MyDialogTitle onClose={onClose} icon={EditOutlined}>
        Actualizar tipo de articulo
      </MyDialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <TipoArticuloForm form={form} handleEdit={true} />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button color={'inherit'} onClick={() => onClose()}>
          Cerrar
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit, onError)}
          startIcon={<SaveOutlined />}
          variant={'contained'}
        >
          Guardar cambios
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TipoArticuloActualizarDialog
